<template>
  <!--
    影片畫質提升（超分／放大）彈窗：生成結果旁邊、任務中心的影片都能用它。
    必須 append-to-body：呼叫方常在 .side-card 裡，它帶 backdrop-filter 會成為 fixed
    包含塊，彈窗會被裁在裡面；因此樣式寫在**非 scoped** 塊裡（見文件末尾）。
  -->
  <el-dialog
    :visible.sync="show"
    title="影片畫質提升"
    width="600px"
    custom-class="upscale-dialog"
    :close-on-click-modal="false"
    append-to-body
    @closed="onClosed"
  >
    <div v-loading="loading" element-loading-text="正在讀取影片資訊…" class="up-body">
      <!-- 結果態：完成後就地預覽 + 下載（使用者不用去素材庫找） -->
      <div v-if="result" class="up-done">
        <video :src="fileUrl(result.localPath || result.path)" controls class="up-video"></video>
        <div class="up-done-line">
          <span class="up-done-tag">已完成</span>
          <span>{{ result.width }}×{{ result.height }} · {{ fmt(result.seconds) }}</span>
          <span v-if="result.points > 0" class="up-done-cost">已扣除 {{ $fmtPoints(result.points) }} 積分</span>
          <!-- 產物是**替換**原影片的（原檔已刪），說一句免得使用者回頭找不到舊版 -->
          <span v-if="result.replaced" class="up-done-cost">原影片已替換</span>
        </div>
      </div>

      <template v-else>
        <div class="up-hint">
          畫質提升 = 把這支影片放大到更高的解析度（高質量插值放大，畫面更細膩；<b>不會憑空生成細節</b>）。
        </div>
        <div v-if="src" class="up-src">
          來源影片：{{ src.width }}×{{ src.height }}
          <span v-if="src.short_side">（短邊 {{ src.short_side }}）</span>
          · {{ fmt(src.seconds) }}
        </div>

        <div class="up-levels">
          <div
            v-for="o in options"
            :key="o.target"
            class="up-level"
            :class="{ on: target === o.target, off: !o.available }"
            @click="pick(o)"
          >
            <div class="up-level-name">{{ o.label }}</div>
            <div class="up-level-price">{{ priceText(o) }}</div>
          </div>
        </div>
        <div v-if="currentReason" class="up-note">{{ currentReason }}</div>

        <div class="up-cost">
          <span v-if="cost > 0">
            本次將扣除 <b>{{ $fmtPoints(cost) }}</b> 積分（{{ src && src.seconds }} 秒 × 每秒 {{ $fmtPoints(currentPer) }} 分）
          </span>
          <span v-else>本次不收費</span>
        </div>
        <div class="up-balance">
          目前餘額 <b :class="{ 'up-danger': notEnough }">{{ $fmtPoints(balance) }}</b> 積分
          <span v-if="notEnough" class="up-danger">（餘額不足，請先儲值）</span>
        </div>
      </template>
    </div>

    <div slot="footer" class="up-foot">
      <template v-if="!result">
        <el-button :disabled="loading || running" @click="show = false">取消</el-button>
        <el-button
          type="primary"
          :loading="running"
          :disabled="loading || !canRun"
          @click="run"
        >{{ running ? '處理中…' : runText }}</el-button>
      </template>
      <template v-else>
        <el-button @click="downloadResult">下載提升後的影片</el-button>
        <el-button type="primary" @click="show = false">完成</el-button>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import { upscaleOptions, upscaleVideo } from '../api/materials'
import { fileUrl, downloadUrl } from '../utils/file'

export default {
  name: 'UpscaleDialog',
  props: {
    visible: { type: Boolean, default: false },
    // 來源影片的**本地相對路徑**（data/outputs/...）—— 後端只認這個，別傳公網地址
    path: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      running: false,
      src: null,          // {seconds,width,height,short_side}
      options: [],        // [{target,label,per_second,points,available,reason}]
      target: '',
      balance: 0,
      result: null        // 提升後的產物
    }
  },
  computed: {
    show: {
      get() { return this.visible },
      set(v) { this.$emit('update:visible', v) }
    },
    current() {
      return (this.options || []).find(o => o.target === this.target) || null
    },
    currentPer() {
      return this.current ? this.current.per_second : 0
    },
    currentReason() {
      const c = this.current
      return c && !c.available ? (c.reason || '該檔位不可用') : ''
    },
    cost() {
      const c = this.current
      return c && c.available ? (Number(c.points) || 0) : 0
    },
    notEnough() {
      return this.cost > 0 && Number(this.balance) < this.cost
    },
    canRun() {
      return !!this.current && this.current.available && !this.notEnough
    },
    runText() {
      if (!this.canRun) {
        return '開始提升'
      }
      return this.cost > 0 ? ('提升並扣除 ' + this.$fmtPoints(this.cost) + ' 積分') : '開始提升'
    }
  },
  watch: {
    visible(v) {
      if (v) {
        this.load()
      }
    }
  },
  methods: {
    fileUrl,
    fmt(t) {
      const s = Number(t) || 0
      return (Math.round(s * 10) / 10) + ' 秒'
    },
    async load() {
      this.result = null
      this.src = null
      this.options = []
      this.target = ''
      this.balance = 0
      if (!this.path) {
        this.$message.warning('沒有可提升畫質的影片')
        return
      }
      this.loading = true
      try {
        const d = (await upscaleOptions(this.path)) || {}
        this.src = {
          seconds: d.seconds, width: d.width, height: d.height, short_side: d.short_side
        }
        this.options = d.options || []
        this.balance = Number(d.balance || 0)
        // 預設選 1080P（最常用的檔位）；它不可用（來源影片已經 ≥1080）就挑第一個可用的
        const prefer = this.options.find(o => o.target === '1080p' && o.available)
        const first = this.options.find(o => o.available)
        const picked = prefer || first || this.options[0] || null
        this.target = picked ? picked.target : ''
      } catch (e) {
        this.$message.error(e.message || '讀取影片資訊失敗')
        this.show = false
      } finally {
        this.loading = false
      }
    },
    pick(o) {
      if (!o.available) {
        if (o.reason) {
          this.$message.warning(o.reason)
        }
        return
      }
      this.target = o.target
    },
    priceText(o) {
      if (!o.available) {
        // 置灰含意要寫明白：不是「壞了」，而是**來源影片已經達到這個解析度**
        return '已達此解析度'
      }
      return o.points > 0 ? (this.$fmtPoints(o.points) + ' 積分') : '免費'
    },
    async run() {
      if (!this.canRun || this.running) {
        return
      }
      const cost = this.cost
      this.running = true
      try {
        const p = (await upscaleVideo(this.path, this.target)) || {}
        this.result = p
        this.$emit('done', p)
        this.$message.success(cost > 0
          ? ('已完成，扣除 ' + this.$fmtPoints(p.points || 0) + ' 積分')
          : '已完成')
      } catch (e) {
        this.$message.error(e.message || '處理失敗，請稍後再試')
      } finally {
        this.running = false
      }
    },
    downloadResult() {
      if (!this.result) {
        return
      }
      const a = document.createElement('a')
      a.href = downloadUrl(this.result.localPath || this.result.path)
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      a.remove()
    },
    onClosed() {
      this.result = null
      this.options = []
    }
  }
}
</script>

<!--
  非 scoped：彈窗 append-to-body 掛到 <body> 下，scoped 的 [data-v-x] 選不中它。
  所有規則以 .upscale-dialog 打頭，避免污染全局。
-->
<style>
.upscale-dialog .up-body {
  min-height: 168px;
}
.upscale-dialog .up-hint {
  margin-bottom: 10px;
  font-size: 12px;
  line-height: 1.6;
  color: var(--c-text-3);
}
.upscale-dialog .up-src {
  margin-bottom: 10px;
  font-size: 13px;
  color: var(--c-text-2);
}
.upscale-dialog .up-levels {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}
.upscale-dialog .up-level {
  padding: 10px 6px;
  border: 1px solid var(--c-border-2);
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease;
}
.upscale-dialog .up-level:hover {
  border-color: var(--c-primary);
}
.upscale-dialog .up-level.on {
  border-color: var(--c-primary);
  background: var(--c-surface-3);
  box-shadow: inset 0 0 0 1px var(--c-primary);
}
.upscale-dialog .up-level.off {
  cursor: not-allowed;
  opacity: 0.55;
  background: var(--c-surface-2);
  /* 虛線邊框 + 灰字：一眼看出「這一檔不能選」，而不是像按鈕壞了 */
  border-style: dashed;
}
.upscale-dialog .up-level.off .up-level-name,
.upscale-dialog .up-level.off .up-level-price {
  color: var(--c-text-3);
  font-weight: 400;
}
.upscale-dialog .up-level-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--c-text-1);
}
.upscale-dialog .up-level-price {
  margin-top: 4px;
  font-size: 12px;
  color: var(--c-text-3);
}
.upscale-dialog .up-note {
  margin-top: 8px;
  font-size: 12px;
  color: var(--c-text-3);
}
.upscale-dialog .up-cost {
  margin-top: 12px;
  font-size: 13px;
  color: var(--c-text-2);
}
.upscale-dialog .up-cost b {
  color: var(--c-primary);
}
.upscale-dialog .up-balance {
  margin-top: 6px;
  font-size: 13px;
  color: var(--c-text-3);
}
.upscale-dialog .up-danger {
  color: var(--c-danger, #f56c6c);
}
.upscale-dialog .up-video {
  display: block;
  width: 100%;
  max-height: 46vh;
  background: #000;
  border-radius: 10px;
}
.upscale-dialog .up-done-line {
  margin-top: 10px;
  font-size: 13px;
  color: var(--c-text-2);
}
.upscale-dialog .up-done-tag {
  display: inline-block;
  margin-right: 8px;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #fff;
  background: var(--c-primary);
}
.upscale-dialog .up-done-cost {
  margin-left: 10px;
  color: var(--c-text-3);
}
/* 窄屏：四檔排兩行（別擠成一條） */
@media (max-width: 680px) {
  .upscale-dialog .up-levels {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
